import React, { useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "./ServiceDetail.css";
import { useNavigate, useParams } from "react-router-dom";

const ServiceDetails = [
  {
    title: "Project Management",
    slug: "project-management",
    metaDescription: "Protek Consult offers end-to-end project management solutions in Hyderabad ensuring timely, cost-effective, and quality-controlled execution of infrastructure and real estate developments.",
    metaKeywords: "project management consultancy Hyderabad, construction project management, infrastructure project planning, real estate project management India, Protek Consult",
    content: (
      <>
        <p>
          We provide end-to-end project management solutions that ensure timely,
          cost-effective, and quality-controlled execution of infrastructure and
          real estate developments. Our structured approach transforms vision
          into delivery.
        </p>
        <h4>What We Deliver:</h4>
        <ul>
          <li>Project planning and milestone scheduling</li>
          <li>Budget control and resource planning</li>
          <li>Construction supervision and quality assurance</li>
          <li>Real-time progress tracking</li>
          <li>Risk identification and mitigation</li>
          <li>Communication with all project stakeholders</li>
        </ul>
        <p>Your project — managed with clarity, control, and commitment.</p>
      </>
    ),
  },
  {
    title: "Design & Architecture",
    slug: "design-and-architecture",
    metaDescription: "Expert architectural design and planning services by Protek Consult in Hyderabad. From concept layouts to construction-ready drawings, we ensure every design meets vision and viability.",
    metaKeywords: "architecture design services Hyderabad, building design India, architectural planning, 3D visualization, master planning, Protek Consult",
    content: (
      <>
        <p>
          Our design and architecture team blends creativity with practicality
          to create spaces that are functional, code-compliant, and contextually
          appropriate. From concept layouts to construction-ready drawings, we
          ensure every design meets both vision and viability.
        </p>
        <h4>Our Capabilities:</h4>
        <ul>
          <li>Master planning and layout design</li>
          <li>Architectural concept and 3D visualization</li>
          <li>Space planning and interior schematics</li>
          <li>Municipal and development plan compliance</li>
          <li>Building code alignment (NBC, UDCPR, IS Codes)</li>
          <li>Design-to-execution documentation</li>
        </ul>
        <p>Designs that inspire — and perform.</p>
      </>
    ),
  },
  {
    title: "Structural Engineering",
    slug: "structural-engineering",
    metaDescription: "Advanced structural engineering services by Protek Consult in Hyderabad. RCC, steel, and hybrid structural design, earthquake-resistant detailing, foundation engineering, and structural audits.",
    metaKeywords: "structural engineering services Hyderabad, RCC structural design, earthquake-resistant design, foundation engineering, structural audit, retrofitting, STAAD ETABS design, Protek Consult",
    content: (
      <>
        <p>
          We offer advanced structural design services tailored for safety,
          durability, and constructability. Whether it's high-rise buildings,
          industrial units, or public infrastructure, our solutions are grounded
          in robust engineering principles.
        </p>
        <h4>Our Expertise Covers:</h4>
        <ul>
          <li>RCC, steel, and hybrid structural design</li>
          <li>Earthquake-resistant design and detailing</li>
          <li>Foundation engineering and soil-structure interaction</li>
          <li>Structural audits and retrofitting assessments</li>
          <li>Peer reviews and code compliance checks</li>
          <li>Design using STAAD, ETABS, SAFE, and REVIT</li>
        </ul>
        <p>Strength, stability, and sustainability in every structure.</p>
      </>
    ),
  },
  {
    title: "Technical Due Diligence",
    slug: "technical-due-diligence",
    metaDescription: "Technical due diligence services by Protek Consult in Hyderabad. Comprehensive legal, technical, and regulatory assessment before land acquisition or investment.",
    metaKeywords: "technical due diligence Hyderabad, land title verification, zoning compliance, regulatory clearance, real estate due diligence India, Protek Consult",
    content: (
      <>
        <p>
          Before you commit to land acquisition or investment, we assess the
          project's feasibility from all angles — legal, technical, and
          regulatory — to protect your interests.
        </p>
        <h4>Key Services:</h4>
        <ul>
          <li>Land title and ownership checks</li>
          <li>Zoning and land use compliance</li>
          <li>Regulatory and environmental clearance review</li>
          <li>Assessment of development potential</li>
          <li>Alignment with master plans and infrastructure</li>
        </ul>
      </>
    ),
  },
  {
    title: "Approval Due Diligence",
    slug: "approval-due-diligence",
    metaDescription: "Approval due diligence services by Protek Consult in Hyderabad. Navigate regulatory frameworks with expert guidance on permits, NOCs, and statutory compliance.",
    metaKeywords: "approval due diligence Hyderabad, building permits India, NOC approval, regulatory compliance, statutory approvals construction, Protek Consult",
    content: (
      <>
        <p>
          Navigating regulatory frameworks is critical in today's infrastructure
          environment. We offer comprehensive approval due diligence services to
          ensure your project is fully compliant and positioned for smooth
          execution.
        </p>
        <h4>Scope of Services:</h4>
        <ul>
          <li>Identification of required permits and NOCs</li>
          <li>Land title and ownership validation</li>
          <li>Zoning, land use, and building regulation checks</li>
          <li>Verification of design compliance with development norms</li>
          <li>Preparation and submission of approval documents</li>
          <li>Monitoring timelines and ensuring statutory compliance</li>
        </ul>
        <p>We handle compliance — so you can focus on construction.</p>
      </>
    ),
  },
  {
    title: "Building & Layout Planning and Permissions",
    slug: "building-layout-planning-permissions",
    metaDescription: "Building and layout planning and permission services by Protek Consult in Hyderabad. Expert site layout design, building planning, DTCP approvals, and regulatory permissions.",
    metaKeywords: "building permission Hyderabad, layout permission, DTCP approval, building planning services, layout planning, municipal approval, HMDA approval, construction permits India, Protek Consult",
    content: (
      <>
        <p>
          At Protek Consult, we provide expert services in site layout design,
          building planning, and regulatory permissions for all types of
          development — residential, commercial, industrial, and institutional.
        </p>
        <p>
          Our approach ensures your project is aesthetically designed,
          functionally optimized, and compliant with all local and statutory
          regulations before construction begins. We work closely with town
          planning authorities, urban local bodies, and development corporations
          to facilitate a smooth, approval-ready process.
        </p>
        <h4>Layout Planning Services</h4>
        <p>
          We prepare well-structured master layout plans that balance land use,
          infrastructure integration, and development control rules.
        </p>
        <h5>Key Offerings:</h5>
        <ul>
          <li>Site assessment and zoning compliance review</li>
          <li>Plot division and road network planning</li>
          <li>Utility service layout (drainage, water supply, power, etc.)</li>
          <li>Open space and amenity reservation as per norms</li>
          <li>Integration with traffic, access, and approach road planning</li>
          <li>
            Preparation of layout drawings for municipal and DTCP approvals
          </li>
          <li>Compliance with UDCPR, local DCR, and planning schemes</li>
        </ul>
        <p>
          We ensure your layout is optimized for utility, regulation, and future
          scalability.
        </p>
        <h4>Building Planning Services</h4>
        <p>
          We design buildings that meet both user needs and building code
          requirements, ensuring constructability and approval-readiness.
        </p>
        <h5>Key Offerings:</h5>
        <ul>
          <li>Building plan design with area efficiency and functionality</li>
          <li>Floor plans, sections, elevations, and façade studies</li>
          <li>FAR/FSI analysis and permissible built-up area calculations</li>
          <li>
            Integration of fire safety, accessibility, ventilation, and lighting
            norms
          </li>
          <li>Staircase, parking, lift, and amenity placement planning</li>
          <li>Preparation of approval-ready architectural plans</li>
        </ul>
        <p>
          From concept to compliance — we create plans that are both visionary
          and viable.
        </p>
        <h4>Permissions & Regulatory Approvals</h4>
        <p>
          We specialize in obtaining building and layout approvals from all
          concerned authorities, helping clients navigate the complex approval
          matrix smoothly.
        </p>
        <h5>Authorities We Coordinate With:</h5>
        <ul>
          <li>Municipal Corporations / Municipalities / Panchayats</li>
          <li>Directorate of Town & Country Planning (DTCP)</li>
          <li>Urban Development Authorities (e.g., HMDA, PMRDA, DDA)</li>
          <li>
            Airports Authority of India, Forest Department, Fire Services, PCB
          </li>
          <li>CRZ, Revenue, Irrigation, and Road Development Authorities</li>
        </ul>
        <h5>Key Approval Services:</h5>
        <ul>
          <li>Building permit and layout sanction applications</li>
          <li>
            Land use conversion (Agricultural to NA/Commercial/Industrial)
          </li>
          <li>Fire, Environmental, and Airport NOCs</li>
          <li>Mutation, sub-division, and amalgamation approvals</li>
          <li>Post-approval modifications and renewal support</li>
          <li>
            Digital document submission and plan uploads as per e-Governance
            systems
          </li>
        </ul>
        <p>
          We ensure your approvals are secured legally, promptly, and without
          risk of rejection.
        </p>
      </>
    ),
  },
  {
    title: "Land & Building Valuation Services",
    slug: "land-building-valuation",
    metaDescription: "Certified land and building valuation services by Protek Consult in Hyderabad. Market-based valuation, FSI/FAR analysis, and compliance reporting for banks, NBFCs, and investors.",
    metaKeywords: "land valuation services Hyderabad, building valuation, property valuation India, FSI FAR analysis, certified valuation, real estate valuation, Protek Consult",
    content: (
      <>
        <p>
          At Protek Consult, we offer certified valuation services to help
          developers, institutions, and investors understand the true worth of
          land and built assets. Our reports are compliant with government,
          financial, and legal standards.
        </p>
        <h4>Key Offerings:</h4>
        <ul>
          <li>Market-based valuation of land and buildings</li>
          <li>Development potential analysis (FSI/FAR utilization)</li>
          <li>DSR and circle rate-based assessments</li>
          <li>Valuation for banks, NBFCs, legal and asset transfers</li>
          <li>Joint venture and redevelopment advisory</li>
          <li>Support for insurance, taxation, and compliance reporting</li>
        </ul>
        <p>Accurate. Transparent. Compliant.</p>
      </>
    ),
  },
  {
    title: "Cost Analysis & Estimation",
    slug: "cost-analysis-estimation",
    metaDescription: "Expert cost analysis and estimation services by Protek Consult in Hyderabad. Quantity take-offs, budgeting, tendering, and value engineering for construction projects.",
    metaKeywords: "cost estimation services Hyderabad, construction cost analysis, quantity surveying, tendering services, value engineering, building cost estimation India, Protek Consult",
    content: (
      <>
        <p>
          We provide expert-level financial assessments and estimations to help
          you plan better and make informed investment decisions.
        </p>
        <h4>Key Services:</h4>
        <ul>
          <li>Land and building valuation as per market and DSR</li>
          <li>Quantity take-offs and material estimation</li>
          <li>Costing and budgeting for all work stages</li>
          <li>Tendering and bid evaluation</li>
          <li>Value engineering to control project costs</li>
        </ul>
      </>
    ),
  },
  {
    title: "Interior Designing & Elevation Landscaping",
    slug: "interior-designing-elevation-landscaping",
    metaDescription: "Interior design and elevation landscaping services by Protek Consult in Hyderabad. Functional and aesthetically refined environments from interiors to open spaces.",
    metaKeywords: "interior designing Hyderabad, elevation design, landscaping services, interior design services India, facade design, sustainable design, Protek Consult",
    content: (
      <>
        <p>
          Beyond structure, we help create functional and aesthetically refined
          environments — from interiors to open spaces.
        </p>
        <h4>Key Services:</h4>
        <ul>
          <li>Interior design concepts and working drawings</li>
          <li>Furniture layout and material specification</li>
          <li>Elevation design (2D/3D facades)</li>
          <li>Hardscape and softscape landscaping</li>
          <li>Outdoor lighting and visual elements</li>
          <li>Sustainable and green design elements</li>
        </ul>
      </>
    ),
  },
  {
    title: "Energy Auditing",
    slug: "energy-auditing",
    metaDescription: "Comprehensive energy auditing services by Protek Consult in Hyderabad. Improve energy efficiency, reduce costs, and meet sustainability goals for industries and buildings.",
    metaKeywords: "energy auditing services Hyderabad, energy efficiency audit, energy conservation, building energy audit India, industrial energy audit, sustainability consulting, Protek Consult",
    content: (
      <>
        <p>
          At Protek Consult, we provide comprehensive energy audit solutions to
          help industries, institutions, and buildings improve energy
          efficiency, reduce operational costs, and meet sustainability goals.
        </p>
        <h4>Key Offerings:</h4>
        <ul>
          <li>Assessment of current energy consumption patterns</li>
          <li>Identification of inefficiencies and wastage points</li>
          <li>Load analysis for electrical systems and equipment</li>
          <li>Recommendations for energy-saving measures and retrofits</li>
          <li>ROI-based analysis for suggested improvements</li>
          <li>Compliance with national energy conservation standards</li>
          <li>Optimizing energy use</li>
        </ul>
        <p>Reducing costs. Driving sustainability.</p>
      </>
    ),
  },
  {
    title: "Land Surveying Services",
    slug: "land-surveying",
    metaDescription: "Accurate land surveying services by Protek Consult in Hyderabad. Topographic surveys, boundary surveys, digital mapping with GPS/DGPS, and construction layout surveys.",
    metaKeywords: "land surveying services Hyderabad, topographic survey, boundary survey, GPS DGPS survey, construction layout survey, digital mapping India, Protek Consult",
    content: (
      <>
        <p>
          At Protek Consult, we deliver accurate and reliable land surveying
          solutions that form the foundation for successful planning, design,
          and execution of infrastructure projects. Our comprehensive surveying
          services ensure precision, compliance, and detailed documentation for
          all your land development needs.
        </p>
        <h4>Key Offerings:</h4>
        <ul>
          <li>Topographic and contour surveys for site planning</li>
          <li>Boundary and demarcation surveys for land ownership clarity</li>
          <li>Construction layout surveys for precise execution</li>
          <li>Digital mapping using GPS, DGPS, and Total Station equipment</li>
          <li>
            Area, volume, and level calculations for design and construction
          </li>
          <li>Compliance with statutory and regulatory land records</li>
        </ul>
        <h4>Our Surveying Expertise:</h4>
        <p>
          We utilize advanced surveying technology and equipment to provide
          accurate measurements and detailed site documentation. Our team
          ensures all surveys meet regulatory standards and provide the precise
          data needed for successful project execution.
        </p>
        <p>Precision surveying for confident development.</p>
      </>
    ),
  },
];

// Slug-to-index mapping for URL resolution
const slugToIndex = {};
ServiceDetails.forEach((service, idx) => {
  slugToIndex[service.slug] = idx;
});

const ServiceDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const sectionRefs = useRef([]);
  const [activeIdx, setActiveIdx] = useState(0);

  // Resolve the service index from slug or numeric ID
  const resolveServiceIndex = (param) => {
    if (param === undefined) return 0;
    // Check if it's a slug
    if (slugToIndex[param] !== undefined) return slugToIndex[param];
    // Fallback to numeric ID for backward compatibility
    const numericId = parseInt(param);
    if (!isNaN(numericId) && numericId >= 0 && numericId < ServiceDetails.length) {
      return numericId;
    }
    return 0;
  };

  // Initialize refs
  useEffect(() => {
    sectionRefs.current = ServiceDetails.map(() => React.createRef());
  }, []);

  // Handle service ID from URL and scroll to specific service
  useEffect(() => {
    if (id !== undefined) {
      const serviceIndex = resolveServiceIndex(id);
      setActiveIdx(serviceIndex);
      // Wait for refs to be initialized and DOM to be ready
      setTimeout(() => {
        if (
          sectionRefs.current[serviceIndex] &&
          sectionRefs.current[serviceIndex].current
        ) {
          sectionRefs.current[serviceIndex].current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    } else {
      // If no specific service is requested, default to first service
      setActiveIdx(0);
    }
  }, [id, sectionRefs.current.length]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to section
  const handleScrollTo = (idx) => {
    if (sectionRefs.current[idx] && sectionRefs.current[idx].current) {
      sectionRefs.current[idx].current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Intersection observer for active section
  useEffect(() => {
    if (sectionRefs.current.length === 0) return; // Wait for refs to be initialized

    const observer = new window.IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          // Find the topmost visible section
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top
              ? prev
              : curr
          );
          const idx = sectionRefs.current.findIndex(
            (ref) => ref.current === topmost.target
          );
          if (idx !== -1 && idx !== activeIdx) {
            setActiveIdx(idx);
          }
        }
      },
      {
        root: null,
        rootMargin: "-50% 0px -50% 0px", // triggers when section is in the middle
        threshold: 0.1,
      }
    );
    sectionRefs.current.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });
    return () => observer.disconnect();
  }, [sectionRefs.current.length, activeIdx]);

  // Get current service for SEO
  const currentService = ServiceDetails[resolveServiceIndex(id)];

  return (
    <>
      <Helmet>
        <title>{currentService.title} | Protek Consult - Hyderabad</title>
        <meta name="description" content={currentService.metaDescription} />
        <meta name="keywords" content={currentService.metaKeywords} />
        <link rel="canonical" href={`https://protekconsult.com/service/${currentService.slug}`} />
        <meta property="og:title" content={`${currentService.title} | Protek Consult`} />
        <meta property="og:description" content={currentService.metaDescription} />
        <meta property="og:url" content={`https://protekconsult.com/service/${currentService.slug}`} />
      </Helmet>
      <div className="service-detail-container">
        <div className="service-detail-left">
          <h2>Our Services</h2>
          <div className="service-list">
            {ServiceDetails.map((service, idx) => (
              <button
                key={idx}
                onClick={() => handleScrollTo(idx)}
                className={activeIdx === idx ? "active" : ""}
              >
                {service.title}
              </button>
            ))}
          </div>
          <button className="back-btn" onClick={() => navigate("/services")}>
            Back to Services
          </button>
        </div>
        <div className="service-detail-right">
          {ServiceDetails.map((service, idx) => (
            <div
              key={idx}
              className="service-section"
              ref={sectionRefs.current[idx]}
            >
              <h2>{service.title}</h2>
              {service.content}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ServiceDetail;
